import { createMaterializer, InvalidationList, Update } from '@wix/materializer'
import { BaseDataItem, DataMapName } from '@wix/thunderbolt-becky-types'
import type { BeckyMaterializer, DataPlanner, Namespaces, Planners } from './types'
import { applyTranslation } from './translateData'

type DataMap = Record<string, BaseDataItem>

export const createBeckyMaterializer = (planners: Planners): BeckyMaterializer => {
	const plannerByNamespace = planners.dataPlanners.reduce(
		(acc, planner) => ({
			...acc,
			[planner.namespace]: planner,
		}),
		{} as Record<string, DataPlanner<any>>
	)

	const materializer = createMaterializer({
		observedRoots: ['data'],
		depth: 3,
		transform: (value: any, [namespace]: Array<string>) => {
			return plannerByNamespace[namespace]?.toViewItem?.(value) ?? value
		},
	})

	type Model = {
		translation: DataMap
		data: Partial<Record<DataMapName, DataMap>> & { document_data: DataMap }
		structure: any
	}

	const rawModel: Model = {
		translation: {},
		data: {
			document_data: {},
		},
		structure: {},
	}

	let update: Update<void | InvalidationList> = materializer.update
	const getTranslatedDataItems = ({
		dataItem,
		translation,
		prevDataItem,
		prevTranslation,
	}: {
		dataItem?: BaseDataItem
		translation: BaseDataItem
		prevDataItem: BaseDataItem
		prevTranslation: BaseDataItem
	}) => {
		const translatedDataItem = translation ? applyTranslation(dataItem, translation) : dataItem
		const translatedPrevValue = prevTranslation ? applyTranslation(prevDataItem, prevTranslation) : prevDataItem

		return { dataItem: translatedDataItem, prevDataItem: translatedPrevValue }
	}

	return {
		get: materializer.get,
		getRawValue: (path: Array<string>) => path.reduce((acc, key) => acc?.[key], rawModel as any),
		batch: (cb: () => void) =>
			materializer.batch((batchUpdate) => {
				update = batchUpdate
				cb()
				update = materializer.update
			}),
		updateData: (type, id, dataItem) => {
			if (!rawModel.data[type]) {
				rawModel.data[type] = {}
			}

			const dataMap: DataMap = rawModel.data[type]!
			let prevDataItem = dataMap[id]

			if (dataItem) {
				dataMap[id] = dataItem
			} else {
				delete dataMap[id]
			}

			const translation = rawModel.translation[id]
			if (type === 'document_data') {
				const translatedItems = getTranslatedDataItems({
					dataItem,
					prevDataItem,
					translation,
					prevTranslation: translation,
				})

				dataItem = translatedItems.dataItem as typeof dataItem
				prevDataItem = translatedItems.prevDataItem
			}

			const partialValue = planners.dataPlanners.reduce(
				(acc, planner) =>
					Object.assign(acc, {
						[planner.namespace]: planner.onUpdate(type, id, dataItem, prevDataItem),
					}),
				{} as Record<Namespaces, any>
			)

			return update(partialValue)
		},
		updateTranslationOverride: (id, translation) => {
			const prevTranslation = rawModel.translation[id]
			rawModel.translation[id] = translation
			const { dataItem, prevDataItem } = getTranslatedDataItems({
				dataItem: rawModel.data.document_data[id],
				prevDataItem: rawModel.data.document_data[id],
				translation,
				prevTranslation,
			})

			const partialValue = planners.dataPlanners.reduce(
				(acc, planner) =>
					Object.assign(acc, {
						[planner.namespace]: planner.onUpdate('document_data', id, dataItem, prevDataItem),
					}),
				{} as Record<Namespaces, any>
			)

			return update(partialValue)
		},
	}
}
